import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import Loading from "../../common/components/loading/loading";
import {
  appendTimeSeriesTooltipOverlay,
  buildTooltipHtml,
  createTooltipHandlers,
  formatTooltipDate,
  formatTooltipNumber,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const GenerationByFuelTypeGraph = ({ data, metricField, title, isLoading }) => {
  const svgRef = useRef();
  const tooltipRef = useRef(null);

  const renderChart = useCallback(() => {
    const svg = d3.select(svgRef.current);
    if (!svg || !svg.node()) return;
    svg.selectAll("*").remove();
    removeTooltip(tooltipRef);

    const parentContainer = svg.node().parentNode;
    const parentWidth = parentContainer.clientWidth;
    const parentHeight = parentContainer.clientHeight;
    const margin = { top: 72, right: 20, bottom: 50, left: 56 };
    const innerWidth = parentWidth - margin.left - margin.right;
    const innerHeight = parentHeight - margin.top - margin.bottom;

    if (!innerHeight || innerHeight <= 0 || !innerWidth) return;

    const chartSvg = svg
      .attr("width", parentWidth)
      .attr("height", parentHeight)
      .attr("viewBox", `0 0 ${parentWidth} ${parentHeight}`)
      .attr("preserveAspectRatio", "none") // Preserve aspect ratio
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const allFuelTypes = [
      "GAS_COGENERATION",
      "GAS_COMBINED_CYCLE",
      "GAS_GAS_FIRED_STEAM",
      "GAS_SIMPLE_CYCLE",
      "HYDRO",
      "WIND",
      "SOLAR",
      //"COAL",
      "OTHER",
      //"DUAL FUEL",
      "ENERGY STORAGE",
    ];

    const fuelTypeColors = {
      GAS_COGENERATION: "navy",
      GAS_COMBINED_CYCLE: "royalblue",
      GAS_GAS_FIRED_STEAM: "skyblue",
      GAS_SIMPLE_CYCLE: "turquoise",
      HYDRO: "pink",
      WIND: "green",
      SOLAR: "yellow",
      //COAL: "red",
      OTHER: "purple",
      //"DUAL FUEL": "orange",
      "ENERGY STORAGE": "darkred",
    };

    data.sort((a, b) => new Date(a.date) - new Date(b.date));

    const groupedData = d3.group(
      data /* .filter((d) => d.fuel_type !== "ENERGY STORAGE") */,
      (d) => new Date(d.date),
    );

    // Aggregate data for each date and fuel type
    const aggregatedData = Array.from(groupedData, ([date, values]) => {
      const fuelTypeMap = new Map();
      values.forEach((v) => {
        const fuelType =
          v.fuel_type === "GAS" ? `GAS_${v.sub_fuel_type}` : v.fuel_type;
        if (!fuelTypeMap.has(fuelType)) {
          fuelTypeMap.set(fuelType, 0);
        }
        fuelTypeMap.set(fuelType, fuelTypeMap.get(fuelType) + v[metricField]);
      });
      return { date, ...Object.fromEntries(fuelTypeMap) };
    });

    // Find fuel types that actually exist in the data
    const existingFuelTypes = allFuelTypes.filter((fuelType) =>
      aggregatedData.some(
        (d) => d[fuelType] !== undefined && d[fuelType] !== 0,
      ),
    );

    const stackedData = d3
      .stack()
      .keys(existingFuelTypes)
      .value((d, key) => d[key] || 0)(aggregatedData);

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(groupedData.keys()))
      .range([0, innerWidth]);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(stackedData, (d) => d3.max(d, (d) => d[1]))])
      .nice()
      .range([innerHeight, 0]);

    
    const defs = chartSvg.append("defs");
    const areaGradientIds = new Map();

    const getAreaGradientId = (fuelType, color) => {
      if (areaGradientIds.has(fuelType)) {
        return areaGradientIds.get(fuelType);
      }

      const gradientId = `generation-area-${fuelType
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")}-${Math.random().toString(36).slice(2)}`;
      const gradient = defs
        .append("linearGradient")
        .attr("id", gradientId)
        .attr("x1", "0%")
        .attr("y1", "0%")
        .attr("x2", "0%")
        .attr("y2", "100%");

      [
        { offset: "0%", opacity: 0.68 },
        { offset: "58%", opacity: 0.38 },
        { offset: "100%", opacity: 0.18 },
      ].forEach(({ offset, opacity }) => {
        gradient
          .append("stop")
          .attr("offset", offset)
          .attr("stop-color", color)
          .attr("stop-opacity", opacity);
      });

      areaGradientIds.set(fuelType, gradientId);
      return gradientId;
    };

    // Add gridlines
    chartSvg
      .append("g")
      .attr("class", "grid  d3-chart-gridline")
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(""));

    const area = d3
      .area()
      .x((d) => xScale(d.data.date))
      .y0((d) => yScale(d[0]))
      .y1((d) => yScale(d[1]))
      .curve(d3.curveLinear);

    const stackedAreaGroup = chartSvg
      .append("g")
      .attr("class", "dashboard-generation-series");

    stackedAreaGroup
      .selectAll("path.dashboard-generation-area")
      .data(stackedData)
      .join("path")
      .attr("class", "dashboard-generation-area")
      .attr("opacity", 1)
      .attr("shape-rendering", "geometricPrecision")
      .attr("fill", ({ key }) => `url(#${getAreaGradientId(key, fuelTypeColors[key])})`)
      .attr("stroke", "none")
      .attr("stroke-width", 0)
      .attr("d", area);

    const { showTooltip, hideTooltip } = createTooltipHandlers({
      tooltipRef,
    });

    appendTimeSeriesTooltipOverlay({
      chartSvg,
      innerWidth,
      innerHeight,
      xScale,
      getData: () => aggregatedData,
      xAccessor: (d) => d.date,
      showTooltip,
      hideTooltip,
      htmlAccessor: (d) =>
        buildTooltipHtml({
          title: formatTooltipDate(d.date, "%Y-%m-%d %H:%M"),
          rows: existingFuelTypes.map((fuelType) => ({
            label: fuelType.replace(/_/g, " ").replace("GAS ", ""),
            value: `${formatTooltipNumber(d[fuelType] ?? 0, {
              maximumFractionDigits: 0,
            })} MW`,
            color: fuelTypeColors[fuelType],
          })),
        }),
    });

    // Add X axis
    chartSvg
      .append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(d3.timeHour.every(6))
          .tickFormat(d3.timeFormat("%H:%M")),
      )
      .attr("class", "d3-chart-axis");

    // Add Y axis
    chartSvg
      .append("g")
      .call(d3.axisLeft(yScale))
      .attr("class", "d3-chart-axis");

    // Y Axis label
    chartSvg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -innerHeight / 2)
      .attr("y", -margin.left + 10)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("class", "d3-chart-axis-label")
      .text("MW");

    // // Add title
    // chartSvg
    //   .append("text")
    //   .attr("x", innerWidth / 2)
    //   .attr("y", -margin.top / 2)
    //   .attr("text-anchor", "middle")
    //   .attr("font-size", "16px")
    //   .attr("font-weight", "bold")
    //   .text(title)
    //   .attr("fill", "grey");

    // Add legend only for existing fuel types
    const legendItemWidth = 150;
    const legendItemHeight = 20;
    const legendItemsPerRow = Math.floor(innerWidth / legendItemWidth);

    const legend = chartSvg
      .append("g")
      .attr("font-family", "sans-serif")
      .attr("font-size", 10)
      .attr("text-anchor", "start")
      .attr("class", "d3-chart-legend-container")
      .selectAll("g")
      .data(existingFuelTypes)
      .join("g")
      .attr(
        "transform",
        (d, i) =>
          `translate(${(i % legendItemsPerRow) * legendItemWidth}, ${
            Math.floor(i / legendItemsPerRow) * legendItemHeight -
            margin.top +
            35
          })`,
      );

    legend
      .append("rect")
      .attr("class", "dashboard-generation-legend-swatch")
      .attr("width", 15)
      .attr("height", 15)
      .attr("rx", 3)
      .attr("ry", 3)
      .attr(
        "fill",
        (d) => `url(#${getAreaGradientId(d, fuelTypeColors[d])})`,
      );

    legend
      .append("text")
      .attr("x", 20)
      .attr("y", 12)
      .attr("class", "d3-chart-legend-label")
      .text((d) => d.replace(/_/g, " ").replace("GAS ", ""));

    // Adjust title position
    chartSvg
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", -margin.top + 20) // Adjusted position
      .attr("text-anchor", "middle")
      .attr("font-size", "16px")
      .attr("font-weight", "bold")
      .text(title)
      .attr("class", "d3-chart-title");
  }, [data, metricField, title]);

  useEffect(() => {
    let resizeTimeout = null;

    const svgNode = svgRef.current; // <- capture current value
    if (!svgNode) return;

    const throttledResize = (entries) => {
      if (!resizeTimeout) {
        resizeTimeout = setTimeout(() => {
          if (!isLoading) {
            renderChart();
          }
          resizeTimeout = null;
        }, 100); // throttle duration in ms
      }
    };

    const observer = new ResizeObserver(throttledResize);

    if (svgNode) {
      observer.observe(svgNode.parentNode);
    }

    return () => {
      if (svgNode && svgNode.parentNode) {
        observer.unobserve(svgNode.parentNode);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    };
  }, [renderChart, isLoading]);

  if (isLoading ?? true) return <Loading />;
  return <svg ref={svgRef} className="dashboard-d3-chart" style={{ width: "100%", height: "100%" }}></svg>;
};

export default GenerationByFuelTypeGraph;




















